import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Plus,
  Edit2,
  Trash2,
  Clock,
  Loader2,
  Save,
  Calendar
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentTypesPage = () => {
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const [types, setTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingType, setEditingType] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    duration_minutes: 30,
    color: '#004aad',
    is_active: true
  });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/booking/types?active_only=false`);
      setTypes(res.data);
    } catch (error) {
      console.error('Error fetching types:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setForm({
        name: type.name,
        description: type.description || '',
        duration_minutes: type.duration_minutes,
        color: type.color,
        is_active: type.is_active
      });
    } else {
      setEditingType(null);
      setForm({
        name: '',
        description: '',
        duration_minutes: 30,
        color: '#004aad',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const saveType = async () => {
    try {
      setSaving(true);
      
      if (editingType) {
        await axios.patch(`${API_URL}/booking/types/${editingType.id}`, form);
      } else {
        await axios.post(`${API_URL}/booking/types`, form);
      }
      
      await fetchTypes();
      setDialogOpen(false);
      
    } catch (error) {
      console.error('Error saving type:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (typeId) => {
    if (!confirm('Désactiver ce type de rendez-vous ?')) return;
    
    try {
      await axios.delete(`${API_URL}/booking/types/${typeId}`);
      await fetchTypes();
    } catch (error) {
      console.error('Error deleting type:', error);
    }
  };

  const colorOptions = [
    '#004aad', // Primary blue
    '#00a86b', // Green
    '#cc007e', // Pink/magenta
    '#f59e0b', // Orange
    '#8b5cf6', // Purple
    '#ef4444', // Red
    '#06b6d4', // Cyan
    '#84cc16', // Lime
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointment-types-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">
            Types de rendez-vous
          </h1>
          <p className="text-gray-500">Configurez les différents types de RDV proposés</p>
        </div>
        <Button onClick={() => openDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau type
        </Button>
      </div>

      {/* Types Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {types.length === 0 ? (
          <Card className="col-span-full border-dashed">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-gray-900 mb-2">
                Aucun type de rendez-vous
              </h3>
              <p className="text-gray-500 mb-4">
                Créez votre premier type de rendez-vous
              </p>
              <Button onClick={() => openDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Créer un type
              </Button>
            </CardContent>
          </Card>
        ) : (
          types.map(type => (
            <Card 
              key={type.id} 
              className={`border-0 shadow-sm ${!type.is_active ? 'opacity-60' : ''}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: type.color + '20' }}
                  >
                    <Calendar className="w-6 h-6" style={{ color: type.color }} />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openDialog(type)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {type.is_active && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteType(type.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                <h3 className="font-heading font-bold text-gray-900 mb-1">
                  {type.name}
                </h3>
                {type.description && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {type.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    {type.duration_minutes} min
                  </div>
                  {!type.is_active && (
                    <Badge variant="outline" className="text-gray-400">
                      Inactif
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Modifier le type' : 'Nouveau type de rendez-vous'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Orientation gratuite"
              />
            </div>
            
            <div>
              <Label htmlFor="description">{t('form_description', lang)}</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description du type de rendez-vous..."
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="duration">Durée (minutes)</Label>
              <Input
                id="duration"
                type="number"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })}
                min={15}
                step={15}
              />
            </div>
            
            <div>
              <Label>Couleur</Label>
              <div className="flex gap-2 mt-2">
                {colorOptions.map(color => (
                  <button
                    key={color}
                    onClick={() => setForm({ ...form, color })}
                    className={`w-8 h-8 rounded-lg transition-transform ${
                      form.color === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {editingType && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Type actif (visible pour les réservations)
                </Label>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveType} disabled={saving || !form.name}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentTypesPage;
